import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "../../router";
import {
  Button,
  Card,
  Empty,
  Image,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import { Context } from "../../context";

import "./styles.css";

const { Title, Text } = Typography;
const { Option } = Select;

/** Dropdown may return Mongo _id or id depending on API shape. */
const getWebseriesOptionValue = (item) => item?._id ?? item?.id;

const buildListParams = (query) => {
  const params = {
    page: query.page,
    pageSize: query.pageSize,
  };

  if (query.seriesId) {
    params.series_id = query.seriesId;
  }

  // List API expects string status values (status=true / status=false return 422).
  if (query.status === "active") {
    params.status = "active";
  } else if (query.status === "inactive") {
    params.status = "inactive";
  }

  if (query.sortBy) {
    params.sort_by = query.sortBy;
    params.sort_order = query.order || "asc";
  }

  return params;
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const extractBulkUploadErrors = (response) => {
  if (!response) return ["Upload failed"];

  if (Array.isArray(response.errors) && response.errors.length > 0) {
    return response.errors;
  }

  if (Array.isArray(response.error) && response.error.length > 0) {
    return response.error;
  }

  if (typeof response.message === "string" && response.message) {
    return [response.message];
  }

  return ["Upload failed"];
};

const ManageReels = () => {
  const navigate = useNavigate();
  const {
    token,
    getAllReels,
    deleteReel,
    getWebseriesDropdown,
    getReelsBulkUploadUrl,
    formatReelsApiError,
    isReelsMutationSuccess,
  } = useContext(Context);

  const [loading, setLoading] = useState(false);
  const [reels, setReels] = useState([]);
  const [webseriesOptions, setWebseriesOptions] = useState([]);

  const [listQuery, setListQuery] = useState({
    page: 1,
    pageSize: 20,
    seriesId: undefined,
    status: "all",
    sortBy: undefined,
    order: undefined,
  });

  const [total, setTotal] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const [preview, setPreview] = useState({
    open: false,
    image: "",
    title: "",
  });

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkUploadResponse, setBulkUploadResponse] = useState("");
  const [bulkUploadErrors, setBulkUploadErrors] = useState([]);

  useEffect(() => {
    const loadWebseries = async () => {
      try {
        const data = await getWebseriesDropdown();
        setWebseriesOptions(data);
      } catch (error) {
        console.error(error);
      }
    };

    loadWebseries();
    // getWebseriesDropdown is a stable App.js export — run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const querySnapshot = {
      page: listQuery.page,
      pageSize: listQuery.pageSize,
      seriesId: listQuery.seriesId,
      status: listQuery.status,
      sortBy: listQuery.sortBy,
      order: listQuery.order,
    };

    const params = buildListParams(querySnapshot);

    const loadReels = async () => {
      try {
        setLoading(true);

        const response = await getAllReels(params, {
          signal: controller.signal,
        });

        setReels(response.reels || []);
        setTotal(response.total || 0);
      } catch (error) {
        if (
          controller.signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error(error);

        message.error(
          formatReelsApiError(error?.data) ||
            error?.message ||
            "Failed to load reels"
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadReels();

    return () => controller.abort();
  }, [
    listQuery.page,
    listQuery.pageSize,
    listQuery.seriesId,
    listQuery.status,
    listQuery.sortBy,
    listQuery.order,
    reloadToken,
  ]);

  const reloadReels = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const handleDelete = async (record) => {
    try {
      await deleteReel(record._id);

      message.success("Reel deleted");

      reloadReels();
    } catch (error) {
      console.error(error);

      message.error(
        formatReelsApiError(error?.data) || error?.message || "Delete failed"
      );
    }
  };

  const handleTableChange = (nextPagination, _filters, sorter) => {
    const page = nextPagination.current;
    const pageSize = nextPagination.pageSize;
    const sorterField = sorter?.field;
    const sorterOrder = sorter?.order;

    setListQuery((prev) => {
      const next = {
        ...prev,
        page,
        pageSize,
      };

      if (sorterField !== undefined) {
        if (sorterOrder) {
          const order = sorterOrder === "ascend" ? "asc" : "desc";
          if (prev.sortBy !== sorterField || prev.order !== order) {
            next.sortBy = sorterField;
            next.order = order;
            next.page = 1;
          }
        } else if (prev.sortBy === sorterField) {
          next.sortBy = undefined;
          next.order = undefined;
          next.page = 1;
        }
      }

      return next;
    });
  };

  const openBulkModal = () => {
    setBulkUploadResponse("");
    setBulkUploadErrors([]);
    setBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkUploadResponse("");
    setBulkUploadErrors([]);
  };

  const handleBulkUploadChange = (info) => {
    if (info.file.status === "uploading") {
      setBulkUploadResponse("");
      setBulkUploadErrors([]);
      return;
    }

    if (info.file.status === "done") {
      const response = info.file.response;

      if (isReelsMutationSuccess(response)) {
        notification.success({
          message: "Success",
          description:
            response?.message || "Reels uploaded successfully",
        });
        closeBulkModal();
        reloadReels();
        return;
      }

      setBulkUploadResponse("");
      setBulkUploadErrors(extractBulkUploadErrors(response));
      return;
    }

    if (info.file.status === "error") {
      const response = info.file.response;
      setBulkUploadResponse("");
      setBulkUploadErrors(extractBulkUploadErrors(response));
    }
  };

  const getSortOrder = (field) => {
    if (listQuery.sortBy !== field) return null;
    return listQuery.order === "asc" ? "ascend" : "descend";
  };

  const columns = [
    {
      title: "Thumbnail",
      dataIndex: "thumbnailImage",
      key: "thumbnailImage",
      width: 100,
      render: (url, record) =>
        url ? (
          <img
            src={url}
            alt={record.title}
            className="reels-thumb"
            onClick={() =>
              setPreview({
                open: true,
                image: url,
                title: record.title,
              })
            }
          />
        ) : (
          <div className="reels-thumb-placeholder">No Image</div>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: true,
      sortOrder: getSortOrder("title"),
    },
    {
      title: "Hindi Title",
      dataIndex: "hindiTitle",
      key: "hindiTitle",
      render: (value) => value || "-",
    },
    {
      title: "Series",
      dataIndex: "seriesTitle",
      key: "seriesTitle",
      render: (value) => value || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      sorter: true,
      sortOrder: getSortOrder("priority"),
      render: (value) => value ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder: getSortOrder("createdAt"),
      render: formatDate,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/reels/edit/${record._id}`)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete Reel"
            onConfirm={() => handleDelete(record)}
          >
            <Button danger type="link">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="reels-page">
      <div className="reels-page-header">
        <div>
          <Title level={4}>Reels Management</Title>
          <Text type="secondary">
            Create, edit and manage reels
          </Text>
        </div>

        <Space wrap>
          <Button icon={<UploadOutlined />} onClick={openBulkModal}>
            Bulk Upload
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/reels/add")}
          >
            Add Reel
          </Button>
        </Space>
      </div>

      <Card>
        <div className="reels-toolbar">
          <div className="reels-toolbar-left">
            <Select
              allowClear
              showSearch
              placeholder="Filter by webseries"
              optionFilterProp="label"
              style={{ minWidth: 220 }}
              value={listQuery.seriesId}
              onChange={(value) => {
                setListQuery((prev) => ({
                  ...prev,
                  seriesId: value || undefined,
                  page: 1,
                }));
              }}
              options={webseriesOptions.map((item) => ({
                value: getWebseriesOptionValue(item),
                label: item.title,
              }))}
            />

            <Select
              placeholder="Filter by status"
              style={{ minWidth: 140 }}
              value={listQuery.status}
              onChange={(value) =>
                setListQuery((prev) => ({
                  ...prev,
                  status: value,
                  page: 1,
                }))
              }
            >
              <Option value="all">All</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </div>
        </div>

        <Spin spinning={loading}>
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={reels}
            onChange={handleTableChange}
            pagination={{
              current: listQuery.page,
              pageSize: listQuery.pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (count) => `Total ${count} reels`,
            }}
            locale={{
              emptyText: <Empty description="No reels found" />,
            }}
          />
        </Spin>
      </Card>

      <Modal
        title="Bulk Upload Reels"
        open={bulkModalOpen}
        onCancel={closeBulkModal}
        footer={null}
        destroyOnClose
        maskClosable={false}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Upload
            name="file"
            accept=".csv"
            headers={{
              authorization: `Bearer ${token}`,
            }}
            action={getReelsBulkUploadUrl()}
            showUploadList
            maxCount={1}
            onChange={handleBulkUploadChange}
          >
            <Button icon={<UploadOutlined />}>Upload CSV</Button>
          </Upload>

          {bulkUploadResponse && (
            <Text type="success">{bulkUploadResponse}</Text>
          )}

          {bulkUploadErrors.length > 0 && (
            <div className="reels-bulk-errors">
              <Title level={5}>Validation errors</Title>
              <ul>
                {bulkUploadErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        open={preview.open}
        footer={null}
        title={preview.title}
        onCancel={() =>
          setPreview({
            open: false,
            image: "",
            title: "",
          })
        }
      >
        <Image src={preview.image} width="100%" />
      </Modal>
    </div>
  );
};

export default ManageReels;
